import {
  ChartLineIcon,
  ExternalLinkIcon,
  MenuIcon,
  ScanEyeIcon,
  ScrollTextIcon,
  ShieldIcon,
  WandIcon,
} from 'lucide-react'
import { useState } from 'react'

import { SparkRewardsSummary } from '../../types'
import { TopbarRewardsProps } from '../topbar-rewards/TopbarRewards'
import { TopbarMenuPointsItem } from './components/TopbarMenuPointsItem'
import { TopbarMenuRewardsItem } from './components/TopbarMenuRewardsItem'
import { TopbarMenuSparkRewardsItem } from './components/TopbarMenuSparkRewardsItem'
import { BaseMiniAppOptions } from '@/base-miniapp/types'
import { TopbarMenuFarmsItem } from '@/features/topbar/components/topbar-menu/components/TopbarMenuFarmsItem'
import { assets } from '@/ui/assets'
import { Dialog, DialogContent, DialogTitle, DialogTrigger } from '@/ui/atoms/dialog/Dialog'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuItemIcon,
  DropdownMenuTrigger,
} from '@/ui/atoms/dropdown/DropdownMenu'
import { IconButton } from '@/ui/atoms/icon-button/IconButton'
import { ExternalLink } from '@/ui/atoms/link/Link'
import { MenuItem, MenuItemIcon } from '@/ui/atoms/menu-item/MenuItem'
import { Switch } from '@/ui/atoms/switch/Switch'
import { links } from '@/ui/constants/links'
import { BuildInfo } from '@/ui/utils/getBuildInfo'
import { testIds } from '@/ui/utils/testIds'

export interface TopbarMenuProps {
  onSandboxModeClick: () => void
  onWatchWalletModeClick: () => void
  isInSandbox: boolean
  isInWatchWalletMode: boolean
  isMobileDisplay: boolean
  buildInfo: BuildInfo
  sparkRewardsSummary: SparkRewardsSummary
  rewardsInfo: TopbarRewardsProps
  baseMiniAppOptions: BaseMiniAppOptions | undefined
}

export function TopbarMenu({
  isInSandbox,
  isInWatchWalletMode,
  onSandboxModeClick,
  onWatchWalletModeClick,
  buildInfo,
  isMobileDisplay,
  sparkRewardsSummary,
  rewardsInfo,
}: TopbarMenuProps) {
  const [open, setOpen] = useState(false)

  const { sha = 'n/a', buildTime = 'n/a' } = buildInfo

  const triggerButton = (
    <IconButton icon={MenuIcon} variant="tertiary" size="m" data-testid={testIds.component.TopbarMenu.button} />
  )

  function closeMenu() {
    setOpen(false)
  }

  if (isMobileDisplay) {
    return (
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>{triggerButton}</DialogTrigger>
        <DialogContent overlayVariant="default" contentVerticalPosition="bottom" className="p-0">
          <DialogTitle className="border-primary border-b p-5 pt-6">Menu</DialogTitle>

          <TopbarMenuSparkRewardsItem {...sparkRewardsSummary} closeMenu={closeMenu} />
          <TopbarMenuRewardsItem {...rewardsInfo} />
          <TopbarMenuPointsItem closeMenu={closeMenu} />

          <MenuItem
            variant="secondary"
            className="cursor-pointer flex-col items-start rounded-none p-6"
            onClick={onSandboxModeClick}
            withSeparator
          >
            <div className="flex w-full items-center gap-2">
              <MenuItemIcon icon={WandIcon} />
              Sandbox
              <Switch checked={isInSandbox} className="ml-auto" />
            </div>
            <div className="typography-label-3 text-secondary">
              Explore Spark with <br />
              unlimited tokens
            </div>
          </MenuItem>

          {import.meta.env.VITE_FEATURE_WATCH_WALLET === '1' && (
            <MenuItem
              variant="secondary"
              className="cursor-pointer flex-col items-start rounded-none p-6"
              onClick={onWatchWalletModeClick}
              withSeparator
              data-testid={testIds.component.TopbarMenu.watchWalletMenuItem}
            >
              <div className="flex w-full items-center gap-2">
                <MenuItemIcon icon={ScanEyeIcon} />
                Watch Wallet
                <Switch
                  checked={isInWatchWalletMode}
                  className="ml-auto"
                  data-testid={testIds.component.TopbarMenu.watchWalletMenuSwitch}
                />
              </div>
              <div className="typography-label-3 text-secondary">
                View any wallet position <br />
                and balance in read-only mode
              </div>
            </MenuItem>
          )}

          <MenuItem asChild variant="secondary" className="cursor-pointer rounded-none p-6" withSeparator>
            <ExternalLink to={links.sparkExternal.dataDashboard} variant="unstyled">
              <MenuItemIcon icon={ChartLineIcon} />
              Data Dashboard
              <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
            </ExternalLink>
          </MenuItem>

          <MenuItem asChild variant="secondary" className="cursor-pointer rounded-none p-6" withSeparator>
            <ExternalLink to={links.sparkExternal.termsOfUse} variant="unstyled">
              <MenuItemIcon icon={ScrollTextIcon} />
              Terms of Service
              <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
            </ExternalLink>
          </MenuItem>

          <MenuItem asChild variant="secondary" className="cursor-pointer rounded-none p-6" withSeparator>
            <ExternalLink to={links.sparkExternal.privacyPolicy} variant="unstyled">
              <MenuItemIcon icon={ShieldIcon} />
              Privacy Policy
              <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
            </ExternalLink>
          </MenuItem>

          <TopbarMenuFarmsItem closeMenu={closeMenu} />

          <span className="typography-label-4 bg-secondary p-6 text-center text-secondary">
            Built from {sha} at {buildTime}
          </span>
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" variant="secondary" className="flex w-80 flex-col gap-1.5 p-1">
        <DropdownMenuItem
          variant="secondary"
          className="cursor-pointer flex-col items-start py-5"
          onSelect={(event) => {
            onSandboxModeClick()
            // @note prevents dropdown from collapse on switch click
            event.preventDefault()
          }}
        >
          <div className="flex w-full items-center gap-2">
            <DropdownMenuItemIcon icon={WandIcon} />
            Sandbox
            <Switch checked={isInSandbox} className="ml-auto" />
          </div>
          <div className="typography-label-3 text-secondary">
            Explore Spark with <br />
            unlimited tokens
          </div>
        </DropdownMenuItem>

        {import.meta.env.VITE_FEATURE_WATCH_WALLET === '1' && (
          <DropdownMenuItem
            variant="secondary"
            className="cursor-pointer flex-col items-start py-5"
            onSelect={(event) => {
              onWatchWalletModeClick()
              // @note prevents dropdown from collapse on switch click
              event.preventDefault()
            }}
            data-testid={testIds.component.TopbarMenu.watchWalletMenuItem}
          >
            <div className="flex w-full items-center gap-2">
              <DropdownMenuItemIcon icon={ScanEyeIcon} />
              Watch Wallet
              <Switch
                checked={isInWatchWalletMode}
                className="ml-auto"
                data-testid={testIds.component.TopbarMenu.watchWalletMenuSwitch}
              />
            </div>
            <div className="typography-label-3 text-secondary">
              View any wallet position <br />
              and balance in read-only mode
            </div>
          </DropdownMenuItem>
        )}

        <DropdownMenuItem asChild variant="secondary" className="cursor-pointer py-6">
          <ExternalLink to={links.sparkExternal.dataDashboard} variant="unstyled">
            <DropdownMenuItemIcon icon={ChartLineIcon} />
            Data Dashboard
            <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
          </ExternalLink>
        </DropdownMenuItem>

        <DropdownMenuItem asChild variant="secondary" className="cursor-pointer py-6">
          <ExternalLink to={links.sparkExternal.termsOfUse} variant="unstyled">
            <DropdownMenuItemIcon icon={ScrollTextIcon} />
            Terms of Service
            <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
          </ExternalLink>
        </DropdownMenuItem>

        <DropdownMenuItem asChild variant="secondary" className="cursor-pointer py-6">
          <ExternalLink to={links.sparkExternal.privacyPolicy} variant="unstyled">
            <DropdownMenuItemIcon icon={ShieldIcon} />
            Privacy Policy
            <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
          </ExternalLink>
        </DropdownMenuItem>

        <DropdownMenuItem asChild variant="secondary" className="cursor-pointer py-6">
          <ExternalLink to={links.appSky.farms.all} variant="unstyled">
            <img src={assets.page.farms} alt="Farms icon" className="w-[15px]" />
            Farms
            <DropdownMenuItemIcon icon={ExternalLinkIcon} className="ml-auto" />
          </ExternalLink>
        </DropdownMenuItem>

        <span className="typography-label-4 p-5 text-center text-secondary">
          Built from {sha} at {buildTime}
        </span>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

TopbarMenu.displayName = 'TopbarMenu'
